import { useLocation, useNavigate } from 'react-router-dom';
import { routes } from '@routes';
import { isPathBuildable } from '@utils/slugHelpers';

/** The minimal viewer identity a profile-edit link needs (a SlugEntity: id and optional slug). */
interface ProfileEditEntity {
  id: string | null;
  slug?: string | null;
}

/**
 * The shared profile-edit CTA scaffolding, single-sourced for every surface that offers a "complete
 * your profile" step - the question-detail match nudge (MatchStateSection) and the event For-You
 * zero-state gate (ForYouZeroState). Returns:
 *   - `canEdit`: whether a profile-edit path can be built for this viewer. The graceful-degrade gate,
 *     so a null / degraded identity drops the CTA rather than throwing in the path builder on click.
 *   - `openEdit`: navigates to the profile editor, carrying the current location as `returnTo`.
 *
 * One place owns the null rule + the returnTo query param, so a future change to the profile-edit CTA
 * contract (a new param, a different degrade rule) can't silently diverge the two call sites (C5).
 */
export function useProfileEditCta(userEntity: ProfileEditEntity): {
  canEdit: boolean;
  openEdit: () => void;
} {
  const navigate = useNavigate();
  const location = useLocation();
  const canEdit = isPathBuildable(userEntity);
  const openEdit = () => {
    navigate(routes.profile.edit(userEntity, { returnTo: location.pathname + location.search }));
  };
  return { canEdit, openEdit };
}
